import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Grid, Divider, IconButton } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CloseIcon from '@mui/icons-material/Close';
import UserCard from './UserCard';
import AddMemberDialog from './AddMemberDialog';
import axios from 'axios';
import { getBackendUserUrl } from '../../utils/utils';
import { useCommonEventData } from '../../contexts/EventContext';

const TeamGroup = ({ teamName, assignmentGroupId, hostCompanyId, addons, onDelete, onAddMembers, onEditName }) => {
  const { eventId, hostId } = useCommonEventData();
  const [isAddMemberDialogOpen, setIsAddMemberDialogOpen] = useState(false);
  const [groupMembers, setGroupMembers] = useState([]);
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState(teamName);

  const fetchGroupMembers = async () => {
    try {
      const response = await axios.get(`${getBackendUserUrl()}/agenda/assignment_group_members/${assignmentGroupId}`);
      setGroupMembers(response.data);
    } catch (error) {
      console.error('Error fetching group members:', error);
    }
  };

  useEffect(() => {
    if (assignmentGroupId) {
      fetchGroupMembers();
    }
  }, [assignmentGroupId]);

  const handleAddMembers = async (selectedUsers, memberType) => {
    try {
      // First add to assignment group
      await onAddMembers(selectedUsers);

      // Then add as event participants
      for (const user of selectedUsers) {
        const requestData = {
          event_id: eventId,
          user_id: user.userId,
          role: memberType === 'Host' ? 'TeamMember' : 'Collaborator',
          invited_by: hostId
        };

        await axios.post(`${getBackendUserUrl()}/event/event_participants/`, requestData);
      }

      await fetchGroupMembers();
      setIsAddMemberDialogOpen(false);
    } catch (error) {
      console.error('Error adding members:', error);
    }
  };

  const handleNameClick = () => {
    setIsEditingName(true);
    setEditedName(teamName);
  };

  const handleNameChange = (e) => {
    setEditedName(e.target.value);
  };

  const handleNameBlurOrEnter = (e) => {
    if (e.type === 'blur' || (e.type === 'keydown' && e.key === 'Enter')) {
      if (editedName.trim() && editedName !== teamName) {
        onEditName(editedName);
      }
      setIsEditingName(false);
    }
  };

  const handleRemoveMember = async (userId) => {
    try {
      await axios.delete(
        `${getBackendUserUrl()}/agenda/assignment_group_members`,
        { params: { assignment_group_id: assignmentGroupId, user_id: userId } }
      );
      setGroupMembers(prev => prev.filter(m => m.userId !== userId));
    } catch (error) {
      console.error('Error removing member:', error);
    }
  };

  return (
    <Box sx={{ mb: 8 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, justifyContent: 'space-between' }}>
        {isEditingName ? (
          <input
            type="text"
            value={editedName}
            autoFocus
            onChange={handleNameChange}
            onBlur={handleNameBlurOrEnter}
            onKeyDown={handleNameBlurOrEnter}
            style={{ fontWeight: 'bold', fontSize: '1.1em', minWidth: 100 }}
          />
        ) : (
          <Typography variant="h6" fontWeight={700} onClick={handleNameClick} title="Click to edit group name">
            {teamName}
          </Typography>
        )}
        <IconButton 
          onClick={onDelete}
          size="small"
          sx={{ 
            color: 'text.secondary',
            '&:hover': {
              color: 'error.main',
            }
          }}
        >
          <DeleteOutlineIcon />
        </IconButton>
      </Box>
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Button 
          variant="outlined" 
          sx={{ borderRadius: 8, textTransform: 'none', fontWeight: 600 }}
          onClick={() => setIsAddMemberDialogOpen(true)}
        >
          + Add Member
        </Button>
        <Button variant="outlined" sx={{ borderRadius: 8, textTransform: 'none', fontWeight: 600 }}>
          Message Group
        </Button>
      </Box>
      <Grid 
        container 
        spacing={3}
        sx={{ 
          mb: 4,
          '& .MuiGrid-item': {
            display: 'flex',
            justifyContent: 'center'
          }
        }}
      >
        {groupMembers.map((member) => (
          <Grid 
            item 
            xs={12} 
            sm={6} 
            md={4} 
            lg={3} 
            key={member.userId}
            sx={{
              mb: 2,
              '&:hover .remove-member-btn': {
                opacity: 1,
              }
            }}
          >
            <Box sx={{ position: 'relative', width: '100%' }}>
              <IconButton
                className="remove-member-btn"
                size="small"
                sx={{
                  position: 'absolute',
                  top: 6,
                  right: 6,
                  zIndex: 2,
                  background: 'rgba(255,255,255,0.8)',
                  opacity: 0,
                  transition: 'opacity 0.2s',
                  padding: 0,
                  width: 22,
                  height: 22,
                  minWidth: 0,
                  minHeight: 0,
                  '&:hover': { background: 'rgba(255,0,0,0.15)' }
                }}
                onClick={() => handleRemoveMember(member.userId)}
                title="Remove member"
              >
                <CloseIcon sx={{ fontSize: 16 }} />
              </IconButton>
              <UserCard 
                user={member.User}
                sx={{
                  width: '100%',
                  maxWidth: '280px',
                  height: '100%'
                }}
              />
            </Box>
          </Grid>
        ))}
      </Grid>
      <Divider />

      <AddMemberDialog
        open={isAddMemberDialogOpen}
        onClose={() => setIsAddMemberDialogOpen(false)}
        teamName={teamName}
        onAddMembers={handleAddMembers}
        hostCompanyId={hostCompanyId}
        addons={addons}
      />
    </Box>
  );
};

export default TeamGroup; 